import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import PageLayout from '../components/layout/PageLayout';
// import AdSenseAd from '../components/ads/AdSenseAd'; // Removed to prevent AdSense violations
import LoadingSpinner from '../components/common/LoadingSpinner';
import { adService } from '../services/adService';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { ErrorTypes } from '../utils/errorHandler';
import { parseSocietyUrl, getAdUrl } from '../utils/urlHelpers';
import { getAdCoverImageUrl, IMAGE_PLACEHOLDER_PATH } from '../utils/imageFallback';
import AdFilters from '../components/ads/AdFilters';

// Custom hook for debounced search
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const AdsPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [ads, setAds] = useState([]);
  
  // Check if this is a society-specific URL
  const { isSocietySpecific, societySlug } = parseSocietyUrl(location.pathname);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    category: '',
    sortBy: 'created_at',
    priceRange: 'all'
  });
  const [categories, setCategories] = useState([]);
  const { showError, showSuccess } = useToast();
  
  // Debounce search term
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Load categories for dropdown
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categoriesData = adService.getCategories();
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error loading categories:', error);
        setCategories([]);
      }
    };
    loadCategories();
  }, []);

  // Load ads
  const loadAds = useCallback(async (pageNum = 1, reset = false) => {
    try {
      if (reset) {
        setLoading(true);
        setPage(1);
        setError(null); // Clear any previous errors
      } else {
        setLoadingMore(true);
      }

      const params = {
        page: pageNum,
        search: debouncedSearchTerm,
        ordering: filters.sortBy === 'title' ? 'title' : filters.sortBy === 'created_at' ? '-created_at' : '-created_at',
        is_for_all_societies: true // Show ads visible in all societies
      };

      // Add category filter if selected
      if (filters.category) {
        params.category = filters.category;
      }

      const response = await adService.getAds(params);
      
      if (reset) {
        setAds(response.results || response);
      } else {
        setAds(prev => [...prev, ...(response.results || response)]);
      }

      // Check if there are more pages
      setHasMore(response.next ? true : false);
    } catch (error) {
      console.error('Error loading ads:', error);
      
      // Show appropriate error message based on error type
      if (error.type === ErrorTypes.NETWORK_ERROR) {
        showError('Network connection failed. Please check your internet connection.');
      } else if (error.type === ErrorTypes.SERVER_ERROR) {
        showError('Server error occurred. Please try again later.');
      } else {
        showError(error.message || 'Failed to load ads. Please try again.');
      }
      
      setError(error.message || 'Failed to load ads. Please try again.');
      // Don't clear existing ads on error, just show error message
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [debouncedSearchTerm, filters.category, filters.sortBy]);

  // Load ads when search term or filters change
  useEffect(() => {
    loadAds(1, true);
  }, [debouncedSearchTerm, filters.category, filters.sortBy]);

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle category selection
  const handleCategoryChange = (e) => {
    setFilters(prev => ({ ...prev, category: e.target.value }));
  };

  // Handle sort change
  const handleSortChange = (e) => {
    setFilters(prev => ({ ...prev, sortBy: e.target.value }));
  };

  // Handle price range change
  const handlePriceRangeChange = (e) => {
    setFilters(prev => ({ ...prev, priceRange: e.target.value }));
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilters({
      category: '',
      sortBy: 'created_at',
      priceRange: 'all',
    });
  };

  // Handle load more
  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      loadAds(page + 1, false);
      setPage(prev => prev + 1);
    }
  };

  // Format price
  const formatPrice = (price, isNegotiable) => {
    const formattedPrice = new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0
    }).format(price);
    
    return isNegotiable ? `${formattedPrice} (Negotiable)` : formattedPrice;
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    return `${Math.ceil(diffDays / 30)} months ago`;
  };

  if (loading) {
    return (
      <PageLayout title="PakPoints">
        <Helmet>
          <title>Ads - PakPoints</title>
          <meta name="description" content="Buy and sell items in Pakistan. Find great deals on electronics, fashion, home goods, and more in your local community." />
          <meta name="keywords" content="ads, classifieds, buy, sell, Pakistan, marketplace, local deals" />
        </Helmet>
        <div className="flex items-center justify-center min-h-screen">
          <LoadingSpinner size="large" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="PakPoints">
      <Helmet>
        <title>Ads - PakPoints</title>
        <meta name="description" content="Buy and sell items in Pakistan. Find great deals on electronics, fashion, home goods, and more in your local community." />
        <meta name="keywords" content="ads, classifieds, buy, sell, Pakistan, marketplace, local deals" />
        <meta property="og:title" content="Ads - PakPoints" />
        <meta property="og:description" content="Buy and sell items in Pakistan. Find great deals on electronics, fashion, home goods, and more in your local community." />
        <meta property="og:type" content="website" />
      </Helmet>


      {/* Top Ad - Removed to avoid header confusion */}

      <div className="w-full px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="flex gap-6">
          {/* Left Sidebar - Removed ads to avoid navigation confusion */}

          {/* Main Content Area */}
          <div className="flex-1 min-w-0">
            <div className="max-w-7xl mx-auto">
              <div className="mb-4 sm:mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Ads</h1>
                {isAuthenticated && (
                  <button
                    type="button"
                    onClick={() => navigate('/ads/create')}
                    className="inline-flex w-full sm:w-auto items-center justify-center bg-blue-600 text-white px-4 py-2.5 rounded-md hover:bg-blue-700 transition-colors min-h-[44px] text-sm font-medium"
                  >
                    <svg className="w-5 h-5 mr-2 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Create Ad
                  </button>
                )}
              </div>

              <div className="flex flex-col gap-4 lg:flex-row lg:gap-8">
                <div className="w-full shrink-0 lg:w-1/4">
                  <div className="lg:sticky lg:top-20">
                    <AdFilters
                      searchTerm={searchTerm}
                      onSearchChange={handleSearchChange}
                      filters={filters}
                      categories={categories}
                      onCategoryChange={handleCategoryChange}
                      onSortChange={handleSortChange}
                      onPriceRangeChange={handlePriceRangeChange}
                      onClearFilters={handleClearFilters}
                    />
                  </div>
                </div>

                <div className="min-w-0 lg:w-3/4">
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6">
                      {error}
                    </div>
                  )}

                  {/* Results count */}
                  <div className="mb-6">
                    <p className="text-gray-600">
                      {ads && ads.length > 0 ? `Showing ${ads.length} ads` : 'No ads found'}
                      {filters.category && ` in ${filters.category}`}
                      {debouncedSearchTerm && ` matching "${debouncedSearchTerm}"`}
                    </p>
                  </div>

                  {/* Ads Grid */}
                  {ads && ads.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
                      {ads.map((ad, index) => (
                        <React.Fragment key={ad.id}>
                          <div 
                            className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow flex flex-col h-full cursor-pointer overflow-hidden"
                            onClick={() => navigate(getAdUrl(ad, societySlug))}
                          >
                            <div className="w-full h-48 bg-gray-200 relative overflow-hidden">
                              <img
                                src={getAdCoverImageUrl(ad)}
                                alt={ad.title}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.src = IMAGE_PLACEHOLDER_PATH;
                                }}
                              />
                            </div>

                            <div className="p-4 flex flex-col flex-grow">
                              <h3 className="text-base font-semibold text-gray-900 mb-2 line-clamp-3 leading-snug">
                                {ad.title}
                              </h3>
                              <div className="mb-4 flex-grow">
                                <p className="text-sm text-gray-500 mb-3 line-clamp-2">
                                  {ad.description || 'No description available'}
                                </p>
                                
                                {/* Ad Details */}
                                <div className="space-y-1 text-sm text-gray-600">
                                  <div className="flex items-center justify-between">
                                    <span className="font-semibold text-lg text-green-600">
                                      {ad.price ? `PKR ${ad.price.toLocaleString()}` : 'Price on Contact'}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      {ad.created_at ? new Date(ad.created_at).toLocaleDateString() : 'Recently'}
                                    </span>
                                  </div>
                                  <div className="flex items-center">
                                    <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    {ad.society?.name || 'Location not specified'}
                                  </div>
                                  <div className="flex items-center justify-between">
                                  <div className="flex items-center text-sm text-gray-500">
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                    {ad.views || 0} views
                                  </div>
                                </div>
                                </div>
                              </div>

                              {/* Action Buttons */}
                              <div className="mt-auto pt-4 border-t border-gray-200">
                                <div className="flex space-x-2">
                                <button className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 transition-colors">
                                  View Details
                                </button>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* Ad between items removed to avoid content confusion */}
                        </React.Fragment>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <div className="text-gray-400 mb-4">
                        <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No ads found</h3>
                      <p className="text-gray-500">
                        Try adjusting your search criteria or browse all ads.
                      </p>
                    </div>
                  )}

                  {/* Load More Button */}
                  {hasMore && ads && ads.length > 0 && (
                    <div className="text-center mt-12">
                      <button
                        onClick={handleLoadMore}
                        disabled={loadingMore}
                        className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loadingMore ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Loading...
                          </>
                        ) : (
                          'Load More Ads'
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Sidebar - Removed ads to avoid navigation confusion */}
        </div>
      </div>
    </PageLayout>
  );
};

export default AdsPage;
